import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, Phone } from "lucide-react";
import { COUNTRIES, formatNational, maskFor } from "@/lib/countries";
import { useI18n } from "@/context/I18nContext";

/**
 * Phone entry with a country combo directly next to the number input.
 * value = { code, phone } where phone is the raw national digit string.
 * onChange({ code, phone }) fires on any change.
 */
export default function PhoneField({ value, onChange, placeholder }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);
  const country = COUNTRIES.find((c) => c.code === value.code) || COUNTRIES[0];

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter((c) => c.name.toLowerCase().includes(q) || c.dial.includes(q) || c.code.toLowerCase() === q);
  }, [query]);

  const placeholderSample = useMemo(() => {
    const g = maskFor(country.code);
    return g.map((n) => "0".repeat(n)).join(" ");
  }, [country.code]);

  const onDigits = (e) => {
    const raw = e.target.value.replace(/\D/g, "");
    onChange({ code: country.code, phone: raw });
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="flex items-stretch gap-2">
        {/* Country combo */}
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          data-testid="phone-country-combo"
          className="flex items-center gap-1.5 shrink-0 bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-2xl px-3.5 py-3.5 text-[var(--rc-text)] hover:border-[var(--rc-gold)]/50 transition-colors"
        >
          <span className="text-xl leading-none">{country.flag}</span>
          <span className="text-sm font-medium">{country.dial}</span>
          <ChevronDown size={16} className="text-[var(--rc-muted)]" />
        </button>

        {/* Number input with live mask */}
        <div className="relative flex-1">
          <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--rc-muted)] pointer-events-none" />
          <input
            type="tel"
            inputMode="tel"
            value={formatNational(value.phone, country.code)}
            onChange={onDigits}
            placeholder={placeholder || placeholderSample}
            data-testid="phone-number-input"
            className="w-full bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-2xl pl-11 pr-4 py-3.5 text-[var(--rc-text)] placeholder:text-[var(--rc-muted)] focus:outline-none focus:border-[var(--rc-gold)]/60 focus:ring-1 focus:ring-[var(--rc-gold)]/50 transition-colors"
          />
        </div>
      </div>

      {/* Country dropdown */}
      {open && (
        <div className="absolute z-50 mt-2 w-full max-w-sm bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-2xl shadow-2xl overflow-hidden rc-pop" data-testid="phone-country-list">
          <div className="relative p-2 border-b border-[var(--rc-border)]">
            <Search size={16} className="absolute left-5 top-1/2 -translate-y-1/2 text-[var(--rc-muted)]" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("search_country")}
              data-testid="phone-country-search"
              className="w-full bg-[var(--rc-bg)] border border-[var(--rc-border)] rounded-xl pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[var(--rc-gold)]/60"
            />
          </div>
          <div className="max-h-64 overflow-y-auto no-scrollbar">
            {filtered.map((c) => (
              <button
                key={c.code}
                type="button"
                onClick={() => { onChange({ code: c.code, phone: value.phone }); setOpen(false); setQuery(""); }}
                data-testid={`phone-country-${c.code}`}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[var(--rc-bg)] transition-colors ${c.code === country.code ? "bg-[var(--rc-gold)]/10" : ""}`}
              >
                <span className="text-xl">{c.flag}</span>
                <span className="flex-1 text-sm truncate">{c.name}</span>
                <span className="text-sm text-[var(--rc-muted)]">{c.dial}</span>
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="px-4 py-6 text-center text-sm text-[var(--rc-muted)]">{t("no_results")}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
